import React, { useState } from 'react';

import ErrorIcon from 'icons/ic-error-large.svg';
import LoadingIcon from 'icons/loading.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ActionModal from 'components/application/ActionModal';
import ActionModalContent from 'components/application/ActionModalContent';
import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import CheckboxField from 'components/application/CheckboxField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectCoshhContext } from 'components/contexts/ProjectCoshhContext';

import { sendAnalytics } from 'components/helpers/analytics';
import DocumentGeneratorService from 'components/helpers/DocumentGeneratorService';

const defaultDocumentOptions = { sds: false };

export default function ProjectCoshhDownloadModal({
  currentCoshhDocument,
  isOpen,
  onClose,
}) {
  const { user: currentUser } = useCurrentActor();

  const { projectId } = useProjectCoshhContext();

  const [coshhDocumentHasErrored, setCoshhDocumentHasErrored] = useState(false);

  const [documentOptions, setDocumentOptions] = useState(
    defaultDocumentOptions,
  );

  const [isCoshhDocumentGenerating, setIsCoshhDocumentGenerating] =
    useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectCoshh.components.ProjectCoshhDownloadModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  let generator;

  const closeModal = () => {
    onClose(false);

    if (!isCoshhDocumentGenerating || coshhDocumentHasErrored) reset();
  };

  const reset = () => {
    setIsCoshhDocumentGenerating(false);
    setCoshhDocumentHasErrored(false);
    setDocumentOptions(defaultDocumentOptions);
  };

  const handleDownloadClick = () => {
    generateCoshhDocumentPdf();
    setIsCoshhDocumentGenerating(true);
  };

  const successCallback = (docUrl) => {
    closeModal();

    sendAnalytics('Individual COSHH downloaded', {
      currentUser,
      coshhDocumentTitle: currentCoshhDocument.title,
      coshhDocumentId: currentCoshhDocument.id,
    });

    window.location.href = docUrl;
  };

  const failureCallback = () => {
    setCoshhDocumentHasErrored(true);
  };

  const generateCoshhDocumentPdf = () => {
    const generateParams = {
      ...documentOptions,
      coshh_document_id: currentCoshhDocument.id,
    };

    generator = new DocumentGeneratorService({
      generateUrl: `/projects/${projectId}/coshh_document_downloads`,
      generateParams,
      successCallback,
      failureCallback,
    });
    generator.start();
  };

  const handleChange = (event) => {
    const option = event.target.value;
    setDocumentOptions({ ...documentOptions, [option]: event.target.checked });
  };

  return (
    isOpen && (
      <ActionModal closeModal={closeModal} closeable={true} isOpen={isOpen}>
        {isCoshhDocumentGenerating ?
          <ActionModalContent
            bodyIcon={
              coshhDocumentHasErrored ?
                <ErrorIcon
                  className='[&_path]:tw-fill-red-600'
                  height={48}
                  width={48}
                />
              : <LoadingIcon height={48} width={48} />
            }
            bodyText={
              coshhDocumentHasErrored ?
                i18nTShared('errors.tryAgainOrContactSupport')
              : i18nT('generating.text')
            }
            bodyTitle={
              coshhDocumentHasErrored ?
                i18nT('notGenerated')
              : i18nT('generating.title')
            }
            closeButtonText={i18nTShared('cancel')}
            closeModal={closeModal}
            closeable={true}
            iconStyling='tw-apply-loading-spinner--blue-light'
            modifiers={['small']}
            withHeader={false}
          />
        : <div className='action-modal__box action-modal__box--small tw-bg-white'>
            <section className='action-modal__form-body tw-border-grey-100 tw-p-8'>
              <h2 className='modalbox-header__sub-title tw-pb-4 tw-font-inter tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
                {i18nT('title', {
                  coshhDocumentTitle: currentCoshhDocument?.title,
                  interpolation: { escapeValue: false },
                })}
              </h2>
              <div>{i18nT('description')}</div>
              <CheckboxField
                checked={documentOptions.sds}
                className='action-modal__form-checkbox'
                customPadding='tw-pt-4'
                height='short'
                label={i18nT('includeSdsLabel')}
                labelTextProps={{ className: 'tw-font-medium' }}
                name='sds'
                onChange={handleChange}
                value='sds'
              />
            </section>
            <section className='action-modal__form-footer'>
              <TextButton
                className='tw-mr-3'
                color='blue'
                onClick={closeModal}
                size='md'
              >
                {i18nTShared('cancel')}
              </TextButton>
              <FilledButton color='mint' onClick={handleDownloadClick}>
                {i18nTShared('download')}
              </FilledButton>
            </section>
          </div>
        }
      </ActionModal>
    )
  );
}

ProjectCoshhDownloadModal.propTypes = {
  currentCoshhDocument: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
